import { Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { PermissionsService } from '../../../../auth/services/PermissionsService';
import { Interpolation, InterpolationItem } from '../../../../config/i18n/components/Interpolation';
import { PATHS } from '../../../../routing/routes';

type Props = {
  onCreateNewProject: () => Promise<void>;
};

type GetStartedTranslationKey = `getStartedBy_${1 | 2 | 3}`;

export const BlankOrganization = ({ onCreateNewProject }: Props) => {
  const [permissions, setPermissions] = useState<{ canCreateProject: boolean; canDownloadTracers: boolean; canInviteCoworker: boolean }>({
    canCreateProject: false,
    canDownloadTracers: false,
    canInviteCoworker: false,
  });

  useEffect(() => {
    const permissionsService = new PermissionsService();

    const loadPermissions = async () => {
      try {
        const [canCreateProject, canDownloadTracers, canInviteCoworker] = await Promise.all([
          await permissionsService.canCreateProject(),
          await permissionsService.canDownloadTracers(),
          await permissionsService.canInviteCoworker(),
        ]);

        setPermissions({ canCreateProject, canDownloadTracers, canInviteCoworker });
      } catch {
        // we shall live without them
      }
    };

    loadPermissions();

    return () => permissionsService.cancelAllRequests();
  }, []);

  const [components, values] = useMemo(() => {
    const components: InterpolationItem[] = [];
    const values: Record<string, string> = {};
    if (permissions.canCreateProject) {
      const index = components.length;
      components.push({ name: `anchor_${index}`, tabIndex: 0, role: 'button', onClick: () => onCreateNewProject() });
      values[`anchor_${index}`] = t('getStartedBy.createProject');
    }
    if (permissions.canInviteCoworker) {
      const index = components.length;
      components.push({ name: `anchor_${index}`, tabIndex: 0, href: PATHS.USERS });
      values[`anchor_${index}`] = t('getStartedBy.addUser');
    }
    if (permissions.canDownloadTracers) {
      const index = components.length;
      components.push({ name: `anchor_${index}`, tabIndex: 0, href: PATHS.SENSORS, target: '_blank' });
      values[`anchor_${index}`] = t('getStartedBy.downloadTools');
    }
    return [components, values];
  }, [permissions.canCreateProject, permissions.canInviteCoworker, permissions.canDownloadTracers, onCreateNewProject]);

  return (
    <Card>
      <CardContent sx={{ textAlign: 'center' }}>
        <Stack sx={{ alignItems: 'center', py: 2 }} spacing={4}>
          <Typography variant="h2" sx={{ fontSize: '2.125rem' }}>
            {t('welcome')}
          </Typography>
          {permissions.canCreateProject && (
            <Button variant="contained" onClick={() => onCreateNewProject()}>
              {t('createNewProject')}
            </Button>
          )}
          <Typography variant="body1" sx={{ color: 'text.primary', fontSize: '0.875rem', fontWeight: 500 }}>
            {t('emptyWorkspace')}
          </Typography>
          {components.length > 0 && (
            <Interpolation
              i18nKey={`getStartedBy_${components.length}` as GetStartedTranslationKey}
              items={components}
              sx={{ color: 'text.secondary', fontSize: '0.875rem', maxWidth: '23.75rem' }}
              values={values}
              variant="body1"
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
